/**
 * Cinematic Studio store.
 *
 * Holds every authored scene plus the transient editing/playback state. Only
 * `scenes` and `activeSceneId` are persisted — tool mode, selection and the
 * playback clock must not survive a reload.
 *
 * Bump `PERSIST_KEY` on any change to the persisted shape so a warm tab
 * re-derives cleanly instead of rehydrating a stale document (the same
 * discipline as the event planner's `masters-event-planner-v3`).
 */
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { makeId } from '@/features/flythrough'
import {
  CINEMATIC_SCENE_VERSION,
  DEFAULT_PATH,
  DEFAULT_SETTINGS,
  type CinematicAircraft,
  type CinematicPath,
  type CinematicPathWaypoint,
  type CinematicScene,
  type CinematicSceneSettings,
} from '../types'

const PERSIST_KEY = 'planmax-cinematic-studio-v1'

/** What a click on the map does. */
export type StudioTool = 'select' | 'place' | 'path'

export type StudioPlayback = 'paused' | 'playing'

interface StudioState {
  scenes: Record<string, CinematicScene>
  activeSceneId: string | null

  // Transient
  tool: StudioTool
  /** Aircraft type armed for placement while `tool === 'place'`. */
  placingTypeCode: string | null
  selectedActorId: string | null
  selectedWaypointId: string | null
  playback: StudioPlayback
  currentMs: number
  speed: number

  // Scene CRUD
  createScene: (name: string, locationId: string | null, locationSlug: string | null, origin: [number, number] | null) => string
  importScene: (scene: CinematicScene) => string
  renameScene: (id: string, name: string) => void
  duplicateScene: (id: string) => string | null
  deleteScene: (id: string) => void
  setActiveScene: (id: string | null) => void
  updateSettings: (patch: Partial<CinematicSceneSettings>) => void
  /** Re-stamp origin/location when a scene is opened at a different airfield. */
  bindSceneToLocation: (locationId: string | null, locationSlug: string | null, origin: [number, number] | null) => void

  // Actor CRUD
  addAircraft: (typeCode: string, position: [number, number], headingDeg: number) => string | null
  updateAircraft: (id: string, patch: Partial<CinematicAircraft>) => void
  deleteAircraft: (id: string) => void
  duplicateAircraft: (id: string) => void

  // Path editing
  ensurePath: (actorId: string) => void
  clearPath: (actorId: string) => void
  updatePath: (actorId: string, patch: Partial<CinematicPath>) => void
  addPathWaypoint: (actorId: string, pos: [number, number]) => void
  updatePathWaypoint: (actorId: string, wpId: string, patch: Partial<CinematicPathWaypoint>) => void
  deletePathWaypoint: (actorId: string, wpId: string) => void

  // Tool + playback
  setTool: (tool: StudioTool) => void
  armPlacement: (typeCode: string | null) => void
  selectActor: (id: string | null) => void
  selectWaypoint: (id: string | null) => void
  play: () => void
  pause: () => void
  seek: (ms: number) => void
  setSpeed: (speed: number) => void
}

function emptyScene(
  name: string,
  locationId: string | null,
  locationSlug: string | null,
  origin: [number, number] | null
): CinematicScene {
  const now = new Date().toISOString()
  return {
    meta: {
      id: makeId('scene'),
      name,
      schemaVersion: CINEMATIC_SCENE_VERSION,
      locationId,
      locationSlug,
      origin,
      createdAt: now,
      updatedAt: now,
    },
    settings: { ...DEFAULT_SETTINGS },
    aircraft: [],
    playlists: [],
  }
}

/** Apply `fn` to the active scene, stamping `updatedAt`. No-op when none. */
function withActiveScene(
  state: StudioState,
  fn: (s: CinematicScene) => CinematicScene
): Partial<StudioState> {
  const id = state.activeSceneId
  if (!id) return {}
  const scene = state.scenes[id]
  if (!scene) return {}
  const next = fn(scene)
  return {
    scenes: {
      ...state.scenes,
      [id]: { ...next, meta: { ...next.meta, updatedAt: new Date().toISOString() } },
    },
  }
}

/** Apply `fn` to one actor of the active scene. */
function withActor(
  state: StudioState,
  actorId: string,
  fn: (a: CinematicAircraft) => CinematicAircraft
): Partial<StudioState> {
  return withActiveScene(state, (scene) => ({
    ...scene,
    aircraft: scene.aircraft.map((a) => (a.id === actorId ? fn(a) : a)),
  }))
}

export const useStudioStore = create<StudioState>()(
  devtools(
    persist(
      (set, get) => ({
        scenes: {},
        activeSceneId: null,
        tool: 'select',
        placingTypeCode: null,
        selectedActorId: null,
        selectedWaypointId: null,
        playback: 'paused',
        currentMs: 0,
        speed: 1,

        // ── Scenes ────────────────────────────────────────────────────────
        createScene: (name, locationId, locationSlug, origin) => {
          const scene = emptyScene(name, locationId, locationSlug, origin)
          set(
            (s) => ({
              scenes: { ...s.scenes, [scene.meta.id]: scene },
              activeSceneId: scene.meta.id,
              selectedActorId: null,
              selectedWaypointId: null,
              currentMs: 0,
              playback: 'paused',
            }),
            false,
            'studio/createScene'
          )
          return scene.meta.id
        },

        importScene: (scene) => {
          set(
            (s) => ({
              scenes: { ...s.scenes, [scene.meta.id]: scene },
              activeSceneId: scene.meta.id,
              selectedActorId: null,
              selectedWaypointId: null,
              currentMs: 0,
              playback: 'paused',
            }),
            false,
            'studio/importScene'
          )
          return scene.meta.id
        },

        renameScene: (id, name) =>
          set(
            (s) => {
              const scene = s.scenes[id]
              if (!scene) return {}
              return { scenes: { ...s.scenes, [id]: { ...scene, meta: { ...scene.meta, name } } } }
            },
            false,
            'studio/renameScene'
          ),

        duplicateScene: (id) => {
          const source = get().scenes[id]
          if (!source) return null
          const now = new Date().toISOString()
          const copy: CinematicScene = {
            ...structuredClone(source),
            meta: {
              ...source.meta,
              id: makeId('scene'),
              name: `${source.meta.name} copy`,
              createdAt: now,
              updatedAt: now,
            },
          }
          set(
            (s) => ({ scenes: { ...s.scenes, [copy.meta.id]: copy }, activeSceneId: copy.meta.id }),
            false,
            'studio/duplicateScene'
          )
          return copy.meta.id
        },

        deleteScene: (id) =>
          set(
            (s) => {
              const scenes = { ...s.scenes }
              delete scenes[id]
              const remaining = Object.keys(scenes)
              return {
                scenes,
                activeSceneId: s.activeSceneId === id ? (remaining[0] ?? null) : s.activeSceneId,
                selectedActorId: null,
                selectedWaypointId: null,
              }
            },
            false,
            'studio/deleteScene'
          ),

        setActiveScene: (id) =>
          set(
            { activeSceneId: id, selectedActorId: null, selectedWaypointId: null, currentMs: 0, playback: 'paused' },
            false,
            'studio/setActiveScene'
          ),

        updateSettings: (patch) =>
          set(
            (s) => withActiveScene(s, (scene) => ({ ...scene, settings: { ...scene.settings, ...patch } })),
            false,
            'studio/updateSettings'
          ),

        bindSceneToLocation: (locationId, locationSlug, origin) =>
          set(
            (s) => withActiveScene(s, (scene) => ({
              ...scene,
              meta: { ...scene.meta, locationId, locationSlug, origin },
            })),
            false,
            'studio/bindSceneToLocation'
          ),

        // ── Actors ────────────────────────────────────────────────────────
        addAircraft: (typeCode, position, headingDeg) => {
          const state = get()
          if (!state.activeSceneId) return null
          const actor: CinematicAircraft = {
            id: makeId('act'),
            name: typeCode.toUpperCase(),
            typeCode,
            position,
            headingDeg,
            altitudeFt: 0,
            scale: 1,
            renderMode: 'solid',
            path: null,
          }
          set(
            (s) => ({
              ...withActiveScene(s, (scene) => ({ ...scene, aircraft: [...scene.aircraft, actor] })),
              selectedActorId: actor.id,
            }),
            false,
            'studio/addAircraft'
          )
          return actor.id
        },

        updateAircraft: (id, patch) =>
          set((s) => withActor(s, id, (a) => ({ ...a, ...patch })), false, 'studio/updateAircraft'),

        deleteAircraft: (id) =>
          set(
            (s) => ({
              ...withActiveScene(s, (scene) => ({
                ...scene,
                aircraft: scene.aircraft.filter((a) => a.id !== id),
              })),
              selectedActorId: s.selectedActorId === id ? null : s.selectedActorId,
            }),
            false,
            'studio/deleteAircraft'
          ),

        duplicateAircraft: (id) =>
          set(
            (s) => {
              const sceneId = s.activeSceneId
              if (!sceneId) return {}
              const source = s.scenes[sceneId]?.aircraft.find((a) => a.id === id)
              if (!source) return {}
              // Offset the copy so it doesn't hide exactly beneath the original.
              const copy: CinematicAircraft = {
                ...structuredClone(source),
                id: makeId('act'),
                name: `${source.name} copy`,
                position: [source.position[0] + 60, source.position[1] + 60],
              }
              return {
                ...withActiveScene(s, (scene) => ({ ...scene, aircraft: [...scene.aircraft, copy] })),
                selectedActorId: copy.id,
              }
            },
            false,
            'studio/duplicateAircraft'
          ),

        // ── Paths ─────────────────────────────────────────────────────────
        ensurePath: (actorId) =>
          set(
            (s) => withActor(s, actorId, (a) => {
              if (a.path) return a
              // Seed a short leg from the resting pose so the path is valid the
              // moment it exists (a one-point path can't be flown).
              return {
                ...a,
                path: {
                  ...DEFAULT_PATH,
                  waypoints: [
                    { id: makeId('pw'), pos: a.position, altitudeFt: a.altitudeFt, holdMs: 0 },
                    {
                      id: makeId('pw'),
                      pos: [a.position[0] + 400, a.position[1]],
                      altitudeFt: a.altitudeFt,
                      holdMs: 0,
                    },
                  ],
                },
              }
            }),
            false,
            'studio/ensurePath'
          ),

        clearPath: (actorId) =>
          set((s) => withActor(s, actorId, (a) => ({ ...a, path: null })), false, 'studio/clearPath'),

        updatePath: (actorId, patch) =>
          set(
            (s) => withActor(s, actorId, (a) => (a.path ? { ...a, path: { ...a.path, ...patch } } : a)),
            false,
            'studio/updatePath'
          ),

        addPathWaypoint: (actorId, pos) =>
          set(
            (s) => withActor(s, actorId, (a) => {
              const wp: CinematicPathWaypoint = {
                id: makeId('pw'),
                pos,
                altitudeFt: a.path?.waypoints.at(-1)?.altitudeFt ?? a.altitudeFt,
                holdMs: 0,
              }
              if (!a.path) {
                return {
                  ...a,
                  path: {
                    ...DEFAULT_PATH,
                    waypoints: [
                      { id: makeId('pw'), pos: a.position, altitudeFt: a.altitudeFt, holdMs: 0 },
                      wp,
                    ],
                  },
                }
              }
              return { ...a, path: { ...a.path, waypoints: [...a.path.waypoints, wp] } }
            }),
            false,
            'studio/addPathWaypoint'
          ),

        updatePathWaypoint: (actorId, wpId, patch) =>
          set(
            (s) => withActor(s, actorId, (a) =>
              a.path
                ? {
                    ...a,
                    path: {
                      ...a.path,
                      waypoints: a.path.waypoints.map((w) => (w.id === wpId ? { ...w, ...patch } : w)),
                    },
                  }
                : a
            ),
            false,
            'studio/updatePathWaypoint'
          ),

        deletePathWaypoint: (actorId, wpId) =>
          set(
            (s) => {
              const next = withActor(s, actorId, (a) => {
                if (!a.path) return a
                const waypoints = a.path.waypoints.filter((w) => w.id !== wpId)
                // A path needs two points to be flyable; drop it entirely below that.
                if (waypoints.length < 2) return { ...a, path: null }
                return { ...a, path: { ...a.path, waypoints } }
              })
              return {
                ...next,
                selectedWaypointId: s.selectedWaypointId === wpId ? null : s.selectedWaypointId,
              }
            },
            false,
            'studio/deletePathWaypoint'
          ),

        // ── Tool + playback ───────────────────────────────────────────────
        setTool: (tool) =>
          set(
            (s) => ({ tool, placingTypeCode: tool === 'place' ? s.placingTypeCode : null }),
            false,
            'studio/setTool'
          ),

        armPlacement: (typeCode) =>
          set(
            { placingTypeCode: typeCode, tool: typeCode ? 'place' : 'select' },
            false,
            'studio/armPlacement'
          ),

        selectActor: (id) =>
          set({ selectedActorId: id, selectedWaypointId: null }, false, 'studio/selectActor'),

        selectWaypoint: (id) => set({ selectedWaypointId: id }, false, 'studio/selectWaypoint'),

        play: () => set({ playback: 'playing' }, false, 'studio/play'),
        pause: () => set({ playback: 'paused' }, false, 'studio/pause'),
        seek: (ms) => set({ currentMs: Math.max(0, ms) }, false, 'studio/seek'),
        setSpeed: (speed) => set({ speed }, false, 'studio/setSpeed'),
      }),
      {
        name: PERSIST_KEY,
        // Selection, tool mode and the clock are session-only.
        partialize: (state) => ({ scenes: state.scenes, activeSceneId: state.activeSceneId }),
      }
    ),
    { name: 'CinematicStudioStore' }
  )
)

/** The active scene, or null. */
export function selectActiveScene(state: StudioState): CinematicScene | null {
  return state.activeSceneId ? (state.scenes[state.activeSceneId] ?? null) : null
}

/** The selected actor of the active scene, or null. */
export function selectSelectedActor(state: StudioState): CinematicAircraft | null {
  const scene = selectActiveScene(state)
  if (!scene || !state.selectedActorId) return null
  return scene.aircraft.find((a) => a.id === state.selectedActorId) ?? null
}
